import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../config/firebase';
import { CustomerAddress } from '../types/store';

// جلب عناوين العميل
export const getCustomerAddresses = async (userId: string): Promise<CustomerAddress[]> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return [];
    const userData = userDoc.data();
    return userData.addresses || [];
  } catch (error) {
    console.error('Error fetching addresses:', error);
    return [];
  }
};

// إضافة عنوان جديد
export const addAddress = async (userId: string, address: Omit<CustomerAddress, 'id' | 'createdAt'>): Promise<CustomerAddress> => {
  try {
    const newAddress: CustomerAddress = {
      ...address,
      id: `addr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date()
    };

    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      addresses: arrayUnion(newAddress)
    });

    return newAddress;
  } catch (error) {
    console.error('Error adding address:', error);
    throw error;
  }
};

// تعديل عنوان
export const updateAddress = async (userId: string, updatedAddress: CustomerAddress): Promise<void> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) throw new Error('User not found');

    const userData = userDoc.data();
    const addresses: CustomerAddress[] = userData.addresses || [];

    // حذف القديم وإضافة الجديد
    const oldAddress = addresses.find(a => a.id === updatedAddress.id);
    if (oldAddress) {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        addresses: arrayRemove(oldAddress)
      });
    }

    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      addresses: arrayUnion(updatedAddress)
    });
  } catch (error) {
    console.error('Error updating address:', error);
    throw error;
  }
};

// حذف عنوان
export const deleteAddress = async (userId: string, addressId: string): Promise<void> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) throw new Error('User not found');

    const userData = userDoc.data();
    const addresses: CustomerAddress[] = userData.addresses || [];
    const addressToDelete = addresses.find(a => a.id === addressId);

    if (addressToDelete) {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        addresses: arrayRemove(addressToDelete)
      });
    }
  } catch (error) {
    console.error('Error deleting address:', error);
    throw error;
  }
};

// تعيين العنوان كافتراضي
export const setDefaultAddress = async (userId: string, addressId: string): Promise<void> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) throw new Error('User not found');

    const userData = userDoc.data();
    const addresses: CustomerAddress[] = userData.addresses || [];

    // تحديث جميع العناوين
    const updatedAddresses = addresses.map(addr => ({
      ...addr,
      isDefault: addr.id === addressId
    }));

    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      addresses: updatedAddresses
    });
  } catch (error) {
    console.error('Error setting default address:', error);
    throw error;
  }
};

// الحصول على العنوان الافتراضي
export const getDefaultAddress = async (userId: string): Promise<CustomerAddress | null> => {
  try {
    const addresses = await getCustomerAddresses(userId);
    return addresses.find(addr => addr.isDefault) || addresses[0] || null;
  } catch (error) {
    console.error('Error getting default address:', error);
    return null;
  }
};
